/* eslint-disable vue/valid-v-slot */
<template>
  <div class="risk-assessment pa-4">
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2">Loading risk assessment data...</div>
    </div>

    <div v-else-if="!selectedTopics || selectedTopics.length === 0" class="text-center pa-4">
      <v-alert type="info" variant="tonal" class="mb-4">
        Please complete the topic selection first to see the risk assessment.
      </v-alert>
    </div>

    <div v-else>
      <!-- Show both scores side by side -->
      <div class="risk-score-row d-flex justify-center align-center mb-8" style="gap: 48px;">
        <div class="risk-score-block text-center">
          <div class="risk-score-label mb-1">Overall Risk Score<br/>(All Voted Topics / Wheel)</div>
          <div :class="['global-risk-score-circle', globalRiskColor]">
            <span class="global-risk-score-value">{{ overallRiskScore }}</span>
          </div>
        </div>
        <div class="risk-score-block text-center">
          <div class="risk-score-label mb-1">Initiative Risk Score<br/>(Selected Topics)</div>
          <div :class="['global-risk-score-circle', globalRiskColor]">
            <span class="global-risk-score-value">{{ globalRiskScore }}</span>
          </div>
        </div>
      </div>
      <div class="d-flex justify-center mb-6" style="gap: 16px;">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-icon v-bind="props" color="primary" class="mr-1">mdi-information-outline</v-icon>
          </template>
          <span>
            <b>Overall Risk Score</b> matches the wheel and is the average of all aspect risk scores with votes.<br/>
            <b>Initiative Risk Score</b> is the average for only the selected topics.<br/>
            <span style="font-size: 0.95em; color: #888;">(Low: &lt;3, Medium: 3–5, High: &gt;5)</span>
          </span>
        </v-tooltip>
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-icon v-bind="props" color="primary">mdi-calculator-variant</v-icon>
          </template>
          <span>
            <b>Risk Score Calculation:</b><br/>
            Risk Score = (Gap Weight × Gap Impact + Severity Weight × Severity) × 8<br/>
            <span style="font-size: 0.95em; color: #888;">
              Gap = |Target - Current|<br/>
              Gap Impact = min(Gap / 8, 1)<br/>
              Severity: High/Medium/Low based on gap and scores
            </span>
          </span>
        </v-tooltip>
      </div>
      <template v-if="selectedAspects.length">
        <!-- Risk Impact Matrix (full width) -->
        <v-card class="mb-6">
            <v-card-title>
              <v-icon start icon="mdi-matrix" class="mr-2"></v-icon>
              Risk Impact Matrix
            </v-card-title>
            <v-card-text class="risk-matrix-container">
              <div class="matrix-grid">
                <!-- Categories as columns -->
                <div class="matrix-header">
                  <div class="matrix-cell"></div>
                  <div v-for="category in categories" :key="category.toString()" class="matrix-cell">
                    {{ category }}
                  </div>
                </div>
                <!-- Impacts as rows -->
                <div v-for="impact in impacts" :key="impact.toString()" class="matrix-row">
                  <div class="matrix-cell">{{ impact }}</div>
                  <div v-for="category in categories" :key="category.toString()" class="matrix-cell">
                    <div class="aspect-chips">
                      <v-chip
                      v-for="aspect in processedQualityAspects.filter(a => a.category === category && a.riskLevel === impact)"
                        :key="aspect.id.toString()"
                        :color="getAspectColor(aspect)"
                        variant="elevated"
                        size="small"
                        class="font-weight-medium"
                        :title="getRiskTooltip(aspect)"
                      >
                        {{ aspect.name }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        <!-- Risk Analysis Distribution (below, full width) -->
        <v-card class="mb-6">
            <v-card-title>
              <v-icon start icon="mdi-chart-pie" class="mr-2"></v-icon>
              Risk Analysis Distribution
            </v-card-title>
            <v-card-text>
              <risk-analysis-pie-charts
                :quality-aspects="processedQualityAspects"
                :categories="categories"
                :impacts="impacts"
              />
            </v-card-text>
          </v-card>
      <!-- Quality Aspects Overview -->
      <v-card class="mb-6">
          <v-card-title class="d-flex align-center">
          <v-icon start icon="mdi-format-list-checks" class="mr-2"></v-icon>
          Quality Aspects Overview
        </v-card-title>
        <v-card-text class="pa-0">
            <v-data-table
              :headers="overviewHeaders"
              :items="processedQualityAspects"
              v-model:sort-by="sortBy"
              v-model:sort-desc="sortDesc"
              :search="filterText"
              class="overview-table"
              density="comfortable"
            >
              <!-- eslint-disable vue/valid-v-slot -->
              <template #item.name="{ item }">
                <span class="font-weight-medium">{{ aspectIdToName[item.id] || item.name || item.id }}</span>
              </template>
              <template #item.gap="{ item }">
                <v-chip :color="Number(item.gap || 0) > 2 ? 'error' : Number(item.gap || 0) > 1 ? 'warning' : 'success'" size="small" variant="elevated">
                  {{ Number(item.gap || 0).toFixed(1) }}
                  </v-chip>
              </template>
              <template #item.compositeScore="{ item }">
                <v-chip :color="getAspectColor(item)" size="small" variant="elevated" :title="getRiskTooltip(item)">
                  {{ item.compositeScore || '0.00' }}
                  </v-chip>
              </template>
              <template #item.riskLevel="{ item }">
                <v-chip :color="getAspectColor(item)" size="small" variant="elevated">
                  {{ item.riskLevel || 'Low' }}
                  </v-chip>
              </template>
              <template #item.category="{ item }">
                <v-chip size="small" color="primary" variant="tonal">
                  {{ aspectIdToMainCategory[item.id] || item.category || 'Unknown' }}
                  </v-chip>
                  </template>
              <!-- eslint-enable vue/valid-v-slot -->
            </v-data-table>
            </v-card-text>
          </v-card>
                  </template>
      <template v-else>
        <v-alert type="info" class="mt-4 text-center">
          Please select topics and conduct voting first.<br>
          The overall risk score below is based on all voted topics.
        </v-alert>
            </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue'
import { getDatabase, ref as dbRef, get } from 'firebase/database'
import segmentRiskData from '@/data/segmentRisk.json'
import segmentData from '@/data/segment.json'
import RiskAnalysisPieCharts from './RiskAnalysisPieCharts.vue'
import { calculateCompositeRiskScore, processAllVotedAspects } from '../../utils/riskScore'
import { VIcon } from 'vuetify/components'

// Quality aspect mapping
const qualityAspectMapping = {
  "1_1": { "name": "Functional Completeness", "description": "Degree to which the set of functions covers all specified tasks" },
  "1_2": { "name": "Functional Correctness", "description": "Degree to which the system provides correct results" },
  "1_3": { "name": "Testing Coverage", "description": "Extent of testing coverage across the system" },
  "2_1": { "name": "Time Behavior", "description": "Response and processing times" },
  "2_2": { "name": "Resource Utilization", "description": "Resource usage levels" },
  "2_3": { "name": "Capacity", "description": "Maximum limits that meet requirements" },
  "3_1": { "name": "Co-existence", "description": "Product can perform efficiently with other products" },
  "3_2": { "name": "Interoperability", "description": "System can exchange information with other systems" },
  "4_1": { "name": "Appropriateness", "description": "User interface elements are appropriate" },
  "4_2": { "name": "Learnability", "description": "System can be learned easily" },
  "4_3": { "name": "Operability", "description": "System is easy to operate and control" },
  "5_1": { "name": "Maturity", "description": "System meets reliability needs" },
  "5_2": { "name": "Availability", "description": "System is accessible when required" },
  "5_3": { "name": "Fault Tolerance", "description": "System operates as intended despite faults" },
  "6_1": { "name": "Confidentiality", "description": "Data is accessible only to authorized users" },
  "6_2": { "name": "Integrity", "description": "System prevents unauthorized modifications" },
  "6_3": { "name": "Authentication", "description": "Identity can be proved as claimed" },
  "7_1": { "name": "Modularity", "description": "System composed of discrete components" },
  "7_2": { "name": "Reusability", "description": "Assets can be used in more than one system" },
  "7_3": { "name": "Analyzability", "description": "Impact of changes can be assessed" },
  "8_1": { "name": "Adaptability", "description": "System can be adapted to different environments" },
  "8_2": { "name": "Installability", "description": "System can be successfully installed/uninstalled" },
  "8_3": { "name": "Replaceability", "description": "System can replace another specified software product" }
}

// Extract main categories from segment.json
const mainCategories = segmentData.map(cat => ({
  id: cat.id,
  name: cat.text.en.name
}))

// Build aspectId -> main category and name mapping from segment.json
const aspectIdToMainCategory = {}
const aspectIdToName = {};
segmentData.forEach(cat => {
  if (cat.children) {
    cat.children.forEach(child => {
      aspectIdToMainCategory[child.id] = cat.text.en.name;
      aspectIdToName[child.id] = child.text.en.name;
    })
  }
});

// Sorting/filtering state
const sortOptions = [
  { value: 'name', label: 'Aspect Name' },
  { value: 'riskLevel', label: 'Risk Level' },
  { value: 'compositeScore', label: 'Risk Score' },
  { value: 'category', label: 'Category' },
];
const sortBy = ref([{ key: 'compositeScore', order: 'desc' }]);
const sortDesc = ref(true);
const filterText = ref('');

const overviewHeaders = [
  { title: 'Aspect', align: 'start', key: 'name' },
  { title: 'Current Score', align: 'center', key: 'currentScore' },
  { title: 'Target Score', align: 'center', key: 'targetScore' },
  { title: 'Gap', align: 'center', key: 'gap' },
  { title: 'Risk Score', align: 'center', key: 'compositeScore' },
  { title: 'Risk Level', align: 'center', key: 'riskLevel' },
  { title: 'Category', align: 'start', key: 'category' },
];

export default defineComponent({
  name: 'RiskAssessmentMatrix',
  components: {
    RiskAnalysisPieCharts,
    VIcon
  },
  props: {
    customerId: { type: String, required: true },
    workshopId: { type: String, required: true },
    selectedTopics: { type: Array, required: true, default: () => [] }
  },
  emits: ['data-loaded', 'back'],
  setup(props, { emit }) {
    const loading = ref(true)
    const workshopData = ref({
      qualityAspects: [],
      allVotedAspects: [],
      swotAnalysis: {
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: []
      },
      categories: segmentRiskData.categories || [],
      impacts: segmentRiskData.impacts || [],
      risks: []
    })

    const determineRiskLevel = (compositeScore) => {
      if (compositeScore > 5) return 'High';
      if (compositeScore >= 3) return 'Medium';
      return 'Low';
    };

    // Add hint for score calculation
    const scoreCalculationHint = 'Risk Score = (Gap Weight * Gap Impact + Severity Weight * Severity) * 8';

    const getAspectColor = (aspect) => {
      if (!aspect) return 'grey';
      
      // Calculate composite risk score
      const currentScore = Number(aspect.currentScore || 0);
      const targetScore = Number(aspect.targetScore || 0);
      const compositeScore = calculateCompositeRiskScore(currentScore, targetScore);
      
      // Determine color based on composite score
      if (compositeScore > 5) return 'error';
      if (compositeScore >= 3) return 'warning';
      return 'success';
    };

    const getAspectName = (topicId) => {
      return qualityAspectMapping[topicId]?.name || topicId;
    }

    // NEW: processed arrays
    const allVotedAspects = ref([]); // all voted aspects
    // Always include all selectedTopics, even if no voting data exists
    const selectedAspects = computed(() => {
      if (!props.selectedTopics?.length) return [];
      return props.selectedTopics.map(topicId => {
        const stringTopicId = String(topicId);
        // Try to find in allVotedAspects
        let aspect = allVotedAspects.value.find(a => String(a.id) === stringTopicId);
        if (!aspect) {
          // If not found, create a default aspect with correct name/category
          aspect = {
            id: stringTopicId,
            name: aspectIdToName[stringTopicId] || stringTopicId,
            currentScore: 0,
            targetScore: 0,
            gap: 0,
            riskLevel: 'Low',
            compositeScore: 0,
            category: aspectIdToMainCategory[stringTopicId] || 'Unknown',
            impact: 'Low',
            impactAreas: [],
            recommendations: [],
            risks: []
          };
        }
        return aspect;
      });
    });

    // Use selectedAspects for matrix/table, always with correct name/category
    const processedQualityAspects = computed(() => {
      let arr = selectedAspects.value.map(aspect => ({
        ...aspect,
        name: aspectIdToName[aspect.id] || aspect.name,
        category: aspectIdToMainCategory[aspect.id] || aspect.category
      }));
      // Filter
      if (filterText.value) {
        arr = arr.filter(a =>
          a.name.toLowerCase().includes(filterText.value.toLowerCase()) ||
          a.category.toLowerCase().includes(filterText.value.toLowerCase())
        );
      }
      // Sort
      arr = arr.sort((a, b) => {
        let vA = a[sortBy.value[0].key];
        let vB = b[sortBy.value[0].key];
        if (sortBy.value[0].key === 'compositeScore') {
          vA = Number(vA); vB = Number(vB);
        } else {
          vA = vA || '';
          vB = vB || '';
        }
        if (vA < vB) return sortDesc.value ? 1 : -1;
        if (vA > vB) return sortDesc.value ? -1 : 1;
        return 0;
      });
      return arr;
    });

    const processedSwotAnalysis = computed(() => {
      const aspects = processedQualityAspects.value;
      
      return {
        strengths: aspects.filter(a => (Number(a.currentScore) || 0) >= 6),
        weaknesses: aspects.filter(a => (Number(a.currentScore) || 0) < 4 && (Number(a.gap) || 0) > 0),
        opportunities: aspects.filter(a => (Number(a.gap) || 0) > 2 && a.riskLevel !== 'High'),
        threats: aspects.filter(a => a.riskLevel === 'High' || ((Number(a.gap) || 0) > 3 && (Number(a.currentScore) || 0) < 3))
      };
    });

    const getRiskTooltip = (aspect) => {
      if (!aspect) return '';
      
      const currentScore = Number(aspect.currentScore || 0);
      const targetScore = Number(aspect.targetScore || 0);
      const gap = Math.abs(targetScore - currentScore);
      const gapImpact = Math.pow(gap, 1.5) / Math.pow(8, 1.5);
      const severity = (8 - currentScore) / 8;
      const compositeScore = calculateCompositeRiskScore(currentScore, targetScore);
      
      return `Risk Score Calculation:
Current Score: ${currentScore.toFixed(1)}
Target Score: ${targetScore.toFixed(1)}
Gap: |Target - Current| = ${gap.toFixed(1)}

Gap Impact (60%): ${(gapImpact * 8).toFixed(2)}
Severity Impact (40%): ${(severity * 8).toFixed(2)}

${scoreCalculationHint}

Risk Level Thresholds:
- High: Score > 5
- Medium: 3 ≤ Score ≤ 5
- Low: Score < 3

Final Risk Level: ${determineRiskLevel(compositeScore)}`;
    };

    // Consolidate How to sections
    const consolidateHowToSections = (aspect) => {
      return aspect.recommendations.map(rec => `How to Improve: ${rec}`).join('\n');
    };

    // Global risk score (average of all aspect risk scores for selected topics)
    const globalRiskScore = computed(() => {
      const aspects = selectedAspects.value;
      if (!aspects.length) return '0.00';
      const avg = aspects.reduce((sum, a) => sum + Number(a.compositeScore), 0) / aspects.length;
      return avg.toFixed(2);
    });
    // The overallRiskScore below matches the wheel's center risk score and is always the average of all voted aspects (all results), not just selected topics.
    const overallRiskScore = computed(() => {
      const aspects = allVotedAspects.value;
      if (!aspects.length) return '0.00';
      const avg = aspects.reduce((sum, a) => sum + Number(a.compositeScore), 0) / aspects.length;
      return avg.toFixed(2);
    });
    // Color for global risk score
    const globalRiskColor = computed(() => {
      const score = Number(globalRiskScore.value);
      if (score > 5) return 'error';
      if (score >= 3) return 'warning';
      return 'success';
    });

    async function loadWorkshopData() {
      console.log('Loading workshop data with selectedTopics:', props.selectedTopics);
      try {
        const db = getDatabase();
        const workshopRef = dbRef(db, `customers/${props.customerId}/workshop/${props.workshopId}`);
        const snapshot = await get(workshopRef);
        const existingData = snapshot.val() || {};
        console.log('Loaded workshop data from Firebase:', existingData);
        // Build resultsArray from all results
        const results = existingData.results || {};
        // Process all voted aspects using shared utility
        allVotedAspects.value = processAllVotedAspects(results, segmentRiskData, aspectIdToMainCategory, aspectIdToName);
        // Process selected aspects for matrix/table
        const qualityAspects = props.selectedTopics.map(topicId => {
          const stringTopicId = String(topicId);
          const resultData = results[stringTopicId];
          if (!resultData) return null;
          return allVotedAspects.value.find(a => a.id === stringTopicId);
        }).filter(Boolean);
        // Update workshop data
        workshopData.value = {
          ...workshopData.value,
          qualityAspects,
          allVotedAspects: allVotedAspects.value,
          swotAnalysis: {
            strengths: qualityAspects.filter(a => (Number(a.currentScore) || 0) >= 6),
            weaknesses: qualityAspects.filter(a => (Number(a.currentScore) || 0) < 4 && (Number(a.gap) || 0) > 0),
            opportunities: qualityAspects.filter(a => (Number(a.gap) || 0) > 2 && a.riskLevel !== 'High'),
            threats: qualityAspects.filter(a => a.riskLevel === 'High' || ((Number(a.gap) || 0) > 3 && (Number(a.currentScore) || 0) < 3))
          },
          selectedRisks: qualityAspects.flatMap(aspect => aspect?.risks || []).map(risk => ({
            ...risk,
            qualityAspects: risk.qualityAspects?.map(id => id.toString())
          })),
          lastUpdated: new Date().toISOString()
        };
        emit('data-loaded', workshopData.value);
      } catch (error) {
        console.error('Error loading workshop data:', error);
      } finally {
        loading.value = false;
      }
    }

    watch(() => props.selectedTopics, () => {
      loadWorkshopData();
    }, { deep: true })

    onMounted(() => {
      loadWorkshopData();
      window.scrollTo({ top: 0, behavior: 'auto' });
    })

    return {
      loading,
      workshopData,
      getAspectColor,
      processedQualityAspects,
      processedSwotAnalysis,
      categories: computed(() => mainCategories.map(cat => cat.name)),
      impacts: computed(() => segmentRiskData.impacts || []),
      getRiskTooltip,
      getAspectName,
      consolidateHowToSections,
      globalRiskScore,
      overallRiskScore,
      globalRiskColor,
      allVotedAspects,
      selectedAspects,
      sortOptions,
      sortBy,
      sortDesc,
      filterText,
      aspectIdToMainCategory,
      aspectIdToName,
      overviewHeaders
    }
  }
})
</script>

<style scoped>
.risk-assessment {
  max-width: 1600px;
  margin: 0 auto;
}

.risk-matrix-container {
  min-height: 400px;
  padding: 1rem;
  width: 100%;
  overflow-x: auto;
}

.matrix-grid {
  display: table;
  min-width: 1200px;
  width: max-content;
  border-collapse: collapse;
}

.matrix-header {
  display: table-row;
  background-color: #f5f5f5;
}

.matrix-row {
  display: table-row;
}

.matrix-cell {
  display: table-cell;
  padding: 12px;
  border: 1px solid #e0e0e0;
  vertical-align: top;
  min-width: 120px;
}

.aspect-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.v-card {
  border-radius: 8px;
  margin-bottom: 24px;
  
  .v-card-title {
    font-size: 1.25rem;
    font-weight: 500;
    padding: 16px 24px;
    background-color: rgba(var(--v-theme-primary), 0.05);
    border-bottom: 1px solid rgba(var(--v-border-opacity), var(--v-border-opacity));
  }
  
  .v-card-text {
    padding: 24px;
  }
}

.v-table {
  width: 100%;
  border-collapse: collapse;
}

.v-table th {
  background-color: rgba(var(--v-theme-primary), 0.05);
  font-weight: 500;
  text-align: left;
  padding: 12px 16px;
}

.v-table td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-border-opacity), var(--v-border-opacity));
}

.v-chip {
  margin: 0.25rem;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
}

.v-list-item {
  padding: 0.5rem 1rem;
}

.v-list-item-title {
  font-weight: 500;
}

.v-list-item-subtitle {
  font-size: 0.875rem;
  opacity: 0.75;
}

.text-center {
  text-align: center;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.pa-4 {
  padding: 1rem;
}

.swot-card {
  height: 100%;
  
  .v-card-title {
    font-size: 1.1rem;
  }
  
  .v-list {
    padding: 8px 0;
    
    .v-list-item {
      padding: 8px 16px;
      
      &:not(:last-child) {
        border-bottom: 1px solid rgba(var(--v-border-opacity), var(--v-border-opacity));
      }
    }
  }
}

.risk-score {
  white-space: nowrap;
  font-family: monospace;
}

.global-risk-score-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}
.global-risk-score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 2.2rem;
  font-weight: 700;
  color: #fff;
  transition: background 0.3s;
}
.global-risk-score-circle.success {
  background: #4caf50;
}
.global-risk-score-circle.warning {
  background: #ffc107;
  color: #222;
}
.global-risk-score-circle.error {
  background: #f44336;
}
.global-risk-score-label {
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 0.5rem;
}
.global-risk-score-hint {
  font-size: 0.98rem;
  color: #666;
  margin-top: 0.25rem;
  line-height: 1.3;
}

.overall-risk-score-label {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}
</style> 